import type { FC } from 'react';
import React, { useMemo } from 'react';
import { TooltipPosition, Tooltip, TooltipBodyDirection } from 'venn-ui-kit';
import PercentageSpan from '../percentage-span/PercentageSpan';
import NumericSpan from '../numeric-span/NumericSpan';
import { cn } from '@/lib/utils';

interface AllocationDifferenceTooltipProps {
  value: number;
  difference: number;
  hasDifference: boolean;
  isFillForward?: boolean;
  position?: TooltipPosition;
  isPercentageMode: boolean;
  hidden?: boolean;
  usePortal?: boolean;
  className?: string;
}

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
export const AllocationValue = React.memo(
  ({ isPercentageMode, value, testId }: { isPercentageMode: boolean; value: number; testId?: string }) => (
    <div className="font-bold" data-testid={testId}>
      {isPercentageMode ? (
        <PercentageSpan precision={1}>{value / 100}</PercentageSpan>
      ) : (
        <NumericSpan prefix={value < 0 ? '-$' : '$'}>{Math.abs(value)}</NumericSpan>
      )}
    </div>
  ),
);

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
export const AllocationDifference = React.memo(
  ({ difference, testId, isPercentageMode }: { difference: number; testId?: string; isPercentageMode?: boolean }) => (
    <div className="italic ml-1.5" data-testid={testId}>
      <NumericSpan precision={1} prefix={difference > 0 ? '+' : undefined} suffix={isPercentageMode ? '%' : ''}>
        {difference}
      </NumericSpan>
    </div>
  ),
);

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
const AllocationDifferenceTooltip: FC<React.PropsWithChildren<AllocationDifferenceTooltipProps>> = React.memo(
  ({
    value,
    difference,
    hasDifference,
    children,
    position = TooltipPosition.Top,
    isPercentageMode,
    hidden,
    usePortal = false,
    className = 'allocation-diff-tooltip',
    isFillForward = false,
  }) => {
    const content = useMemo(
      () => (
        <div className={isFillForward ? '[&&]:min-h-[36px]' : undefined}>
          <div
            className={cn('flex min-w-[95px] h-[13px] text-[11px]', isFillForward ? 'justify-start' : 'justify-around')}
          >
            <AllocationValue testId="qa-value" isPercentageMode={isPercentageMode} value={value} />
            {hasDifference && <AllocationDifference testId="qa-diff" difference={difference} />}
          </div>
          {isFillForward ? (
            <div>
              <sup>*</sup>Allocation forward-filled: value will be drifted from prior allocation during analysis.
            </div>
          ) : null}
        </div>
      ),
      [difference, hasDifference, isFillForward, isPercentageMode, value],
    );
    return (
      <Tooltip
        usePortal={usePortal}
        isHidden={hidden}
        bodyDirection={TooltipBodyDirection.Center}
        position={position}
        maxWidth={250}
        content={content}
        className={className}
      >
        {children}
      </Tooltip>
    );
  },
);

export default AllocationDifferenceTooltip;
